import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, FileText, Target, Zap, Shield } from 'lucide-react';

export default function ResumeRewritePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.resumeRewrite} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Professional Resume Service</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Professional Resume Rewrite Service
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Transform your resume into a powerful, ATS-friendly document that gets noticed. Every resume is reviewed by a real person before invoicing.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
              data-testid="rewrite-hero-cta"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* What's Included */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
            What's Included in a Resume Rewrite
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: 'Complete Content Rewrite', desc: 'We rewrite your resume from scratch with achievement-focused bullet points that quantify your impact.' },
              { icon: Target, title: 'ATS-Friendly Formatting', desc: 'Clean, single-column layout optimized for Applicant Tracking Systems used by most employers.' },
              { icon: Zap, title: 'Keyword Optimization', desc: 'Strategic placement of industry and role-specific keywords to improve visibility.' },
              { icon: Shield, title: 'Professional Summary', desc: 'A compelling professional summary that positions you for your target roles.' },
              { icon: Check, title: 'Skills Section', desc: 'Organized skills section highlighting technical and soft skills relevant to your goals.' },
              { icon: Check, title: 'PDF & DOCX Delivery', desc: 'Receive both formats so you can use your resume immediately and make future edits.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Who This Is For */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Who Resume Rewriting Is For
            </h2>
            
            <div className="space-y-4">
              {[
                'Job seekers whose current resume isn\'t getting responses',
                'Professionals looking to reposition for a new role or industry',
                'Career changers who need to highlight transferable skills',
                'Anyone with an outdated or poorly formatted resume',
                'Job seekers targeting specific roles in customer service, admin, operations, or technical fields',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                  <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
            How the Process Works
          </h2>
          
          <div className="space-y-6">
            {[
              { step: 1, title: 'Submit Your Intake', desc: 'Upload your current resume and tell us about your goals and target roles.' },
              { step: 2, title: 'We Review Your Materials', desc: 'A real person reviews your resume and determines the right scope.' },
              { step: 3, title: 'Scope Confirmed & Invoice Sent', desc: 'We confirm pricing and send your Stripe invoice. No surprises.' },
              { step: 4, title: 'Payment & Work Begins', desc: 'After payment, we get to work rewriting your resume.' },
              { step: 5, title: 'Delivery & Revisions', desc: 'You receive your new resume with reasonable revisions included within scope.' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 items-start">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Related Services */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/services/linkedin-optimization" className="text-teal-700 hover:text-teal-800 underline">LinkedIn Optimization</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/cover-letter" className="text-teal-700 hover:text-teal-800 underline">Cover Letter Support</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/resume-refresh" className="text-teal-700 hover:text-teal-800 underline">Resume Refresh</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View Pricing</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready for a professional resume rewrite?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and we'll review your materials personally. No payment until scope is confirmed.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
              data-testid="rewrite-bottom-cta"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
